import type { PaymentBlock } from '@/types/sale';

/**
 * What the QRIS panel needs, read from a create / resume response.
 *
 * The backend answers every online payment with a gateway-neutral `payment`
 * block (Midtrans, Xendit or Doku took the money — the tender is still
 * `midtrans`). Older responses, and Midtrans's own fields, are the fallback:
 * `meta.qr_string`, `qris.{format,url}`, `paymentExpire`. Either way this
 * returns ONE shape, so a checkout, an add-on and a resumed order cannot read
 * the same payment three ways.
 */
export interface QrisView {
  qrString: string | null;
  /** Only for an ASPI QR — the border exists only in the gateway's PNG. */
  qrImageUrl: string | null;
  paymentExpire: string | null;
  /** Who took the charge; null on a response that predates the block. */
  gateway: string | null;
  /**
   * A Snap charge's session token — opened with `snap.embed`, NEVER drawn as a
   * QR (see below). Set only for a hosted charge, or an older response that
   * carries a token and no QR payload.
   */
  snapToken: string | null;
}

interface ResponseLike {
  payment?: PaymentBlock | null;
  meta?: { qr_string?: string | null; qrString?: string | null; token?: string | null } | null;
  qris?: { format?: string | null; url?: string | null } | null;
  paymentExpire?: string | null;
}

export function qrisViewOf(res: ResponseLike | null | undefined): QrisView {
  const p = res?.payment ?? null;
  const qr = p?.kind === 'qris' ? p.qr : null;
  // Never a Snap `token`: it is an opaque session id, not an EMV payload, and
  // drawn as a QR it produces a code no banking app can pay.
  const qrString = qr?.string ?? res?.meta?.qr_string ?? res?.meta?.qrString ?? null;
  const format = qr?.format ?? res?.qris?.format ?? null;
  const url = qr?.url ?? res?.qris?.url ?? null;
  return {
    qrString,
    qrImageUrl: format === 'ASPI' ? url : null,
    paymentExpire: p?.expiresAt ?? res?.paymentExpire ?? null,
    gateway: p?.gateway ?? null,
    snapToken:
      p?.kind === 'hosted'
        ? (p.hosted?.token ?? res?.meta?.token ?? null)
        : !p && !qrString
          ? (res?.meta?.token ?? null)
          : null,
  };
}

const GATEWAY_NAME: Record<string, string> = {
  midtrans: 'Midtrans',
  xendit: 'Xendit',
  doku: 'DOKU',
};

/** Display name of the processor that took the charge. */
export function gatewayName(gateway: string | null | undefined): string | null {
  if (!gateway) return null;
  return GATEWAY_NAME[gateway.toLowerCase()] ?? gateway;
}

/**
 * Did the charge run out of time (rather than get refused)?
 *
 * Only the gateway's word counts: Midtrans says `expire`; the neutral block
 * says `EXPIRED` for every gateway. Our own clock reaching zero is a guess —
 * a scan in the final second still settles.
 */
export function isExpiredEnding(
  gatewayWord: string | null | undefined,
  neutral: string | null | undefined,
): boolean {
  return gatewayWord === 'expire' || neutral === 'EXPIRED';
}
